import { useState, useEffect } from "react";
import { getFiltersFromParams } from "../plpHelper";

/**
 * Custom hook to generate and manage filter query state for Psykhe PLP.
 * Filters from urlQueryParams.pages are treated as "hidden" (not shown in UI or URL).
 * @param {object} urlQueryParams - The query params object (may include filters or pages).
 * @returns {[object, function, object, object]} - [filters, setFilters, visibleFilters, hiddenFilters]
 */
export function generateFilterQuery(urlQueryParams) {
  // Hidden filters from pages param
  const hiddenFilters = Array.isArray(urlQueryParams?.pages) && urlQueryParams.pages.length > 0
    ? getFiltersFromParams(urlQueryParams.pages)
    : {};

  // Initial filters: from filters param, URL, or hiddenFilters
  const [filters, setFilters] = useState(() => {
    try {
      if (urlQueryParams?.filters) {
        return { ...hiddenFilters, ...JSON.parse(urlQueryParams.filters) };
      }
      if (typeof window !== "undefined") {
        const url = new URL(window.location.href);
        const filtersParam = url.searchParams.get("filters");
        if (filtersParam) {
          return { ...hiddenFilters, ...JSON.parse(filtersParam), setByUser: false };
        }
      }
      return { ...hiddenFilters };
    } catch (error) {
      console.error("Failed to parse filters:", error);
      return { ...hiddenFilters };
    }
  });

 const visibleFilters = Object.fromEntries(
  Object.entries(filters)
    .filter(([k]) => k !== "setByUser")
    .map(([k, v]) => {
      if (hiddenFilters[k]) {
        // Remove hidden values from the array
        const hiddenVals = hiddenFilters[k].map(x => x.toLowerCase());
        const visibleVals = v.filter(
          val => !hiddenVals.includes(val.toLowerCase())
        );
        return [k, visibleVals];
      }
      return [k, v];
    })
    .filter(([k, v]) => Array.isArray(v) ? v.length > 0 : !!v)
);

  // When filters change, update the URL with only visible filters
  useEffect(() => {
    if (typeof window === "undefined") return;
    const url = new URL(window.location.href);
    if (Object.keys(visibleFilters).length === 0) {
      url.searchParams.delete("filters");
    } else {
      url.searchParams.set("filters", JSON.stringify(visibleFilters));
    }
    window.history.replaceState({}, '', url);
  }, [visibleFilters]);

  return [filters, setFilters, visibleFilters, hiddenFilters];
}